import type { TFunction } from "i18next";
import type { TeamPlayer } from "../../types";
import PlayerChip from "./PlayerChip";
import type { LineupChip, UseLineupDndResult } from "./useLineupDnd";

export interface LineupEditingState {
  teamType: "home" | "away";
  playerId: string;
  column: "starters" | "substitutes" | "excluded";
  jersey_number: number;
  player_name: string;
}

interface StartersAreaProps {
  teamType: "home" | "away";
  chips: LineupChip[];
  dnd: UseLineupDndResult;
  readOnly: boolean;
  editingPlayer: LineupEditingState | null;
  onStartEdit: (teamType: "home" | "away", player: TeamPlayer) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditJerseyChange: (value: number) => void;
  onEditNameChange: (value: string) => void;
  t: TFunction;
}

export default function StartersArea({
  teamType,
  chips,
  dnd,
  readOnly,
  editingPlayer,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditJerseyChange,
  onEditNameChange,
  t,
}: StartersAreaProps) {
  const zoneProps = dnd.zoneProps("starters");
  const isDropHover = dnd.dragOverZone === "starters";

  return (
    <div>
      <h4 className="font-medium mb-3">{t("starters")}</h4>
      <div
        {...zoneProps}
        data-testid={`${teamType}-starters-candidates`}
        className={`space-y-2 min-h-[3rem] rounded-lg p-1 transition-colors md:max-h-[26rem] md:overflow-y-auto md:pr-1 lg:max-h-[32rem] ${
          isDropHover ? "bg-emerald-50 ring-2 ring-emerald-300" : ""
        }`}
      >
        {chips.length === 0 && (
          <p className="text-xs text-gray-400 italic p-2">
            {t("lineupBoard.emptyStarters", {
              defaultValue: "Drag or tap a bench player to add a starter.",
            })}
          </p>
        )}
        {chips.map(({ player, selectable }) => {
          const isEditing =
            editingPlayer?.teamType === teamType &&
            editingPlayer?.column === "starters" &&
            editingPlayer?.playerId === player.player_id;
          return (
            <PlayerChip
              key={player.player_id}
              player={player}
              zone="starters"
              readOnly={readOnly}
              toggleDisabled={!selectable}
              isDragging={dnd.draggingPlayerId === player.player_id}
              dragProps={dnd.chipProps(player, "starters")}
              isEditing={Boolean(isEditing)}
              editValues={
                isEditing && editingPlayer
                  ? {
                      jersey_number: editingPlayer.jersey_number,
                      player_name: editingPlayer.player_name,
                    }
                  : null
              }
              onStartEdit={() => onStartEdit(teamType, player)}
              onCancelEdit={onCancelEdit}
              onSaveEdit={onSaveEdit}
              onEditJerseyChange={onEditJerseyChange}
              onEditNameChange={onEditNameChange}
              t={t}
            />
          );
        })}
      </div>
    </div>
  );
}
